'use client';

// i18n trang /sale (khách + môi giới) — 6 ngôn ngữ khách dùng nhiều nhất ở GMS (thống kê Firestore
// 2026-11-03, 3.242 booking: vi 978 · en 927 · zh 328 · ko 136 · fr 121 · ja 110).
// KHÁC cổng /g (lib/portal-i18n.ts: EN/VI tĩnh + Haiku dịch động có cache): /sale là trang PUBLIC không
// token nên không có chỗ ghi cache dịch → dùng từ điển TĨNH, viết tay, ghép từ các file `sale-i18n-*.ts`.
// Chỉ dịch CHROME giao diện; tên căn/mô tả/tiện ích là dữ liệu của tenant, giữ nguyên.
//
// Thêm chuỗi: khai `key: { vi, en, zh, ko, fr, ja }` trong file dict tương ứng — thiếu ngôn ngữ nào thì
// tự rơi về vi (xem `translate`). Biến chèn dùng {name}: t('key', { name: 'x' }).

import { useCallback, useSyncExternalStore } from 'react';
import { SALE_DICT as PAGE_DICT } from './sale-i18n-page';
import { SALE_DICT as BOOKING_DICT } from './sale-i18n-booking';
import { SALE_DICT as MISC_DICT } from './sale-i18n-misc';

export type SaleLang = 'vi' | 'en' | 'zh' | 'ko' | 'fr' | 'ja';
export type SaleEntry = Partial<Record<SaleLang, string>> & { vi: string };
export type SaleDict = Record<string, SaleEntry>;

export const SALE_LANGS: { code: SaleLang; label: string; name: string; flag: string; intl: string }[] = [
  { code: 'vi', label: 'VI', name: 'Tiếng Việt', flag: '🇻🇳', intl: 'vi-VN' },
  { code: 'en', label: 'EN', name: 'English', flag: '🇬🇧', intl: 'en-US' },
  { code: 'zh', label: '中文', name: '中文（简体）', flag: '🇨🇳', intl: 'zh-CN' },
  { code: 'ko', label: '한국어', name: '한국어', flag: '🇰🇷', intl: 'ko-KR' },
  { code: 'fr', label: 'FR', name: 'Français', flag: '🇫🇷', intl: 'fr-FR' },
  { code: 'ja', label: '日本語', name: '日本語', flag: '🇯🇵', intl: 'ja-JP' },
];

const STORAGE_KEY = 'gms-sale-lang';
const isSaleLang = (v: unknown): v is SaleLang => SALE_LANGS.some(l => l.code === v);

/** Ngôn ngữ trình duyệt → ngôn ngữ /sale hỗ trợ. Không hỗ trợ (ja, de…) → en (khách quốc tế đọc được nhiều nhất). */
export function langFromNavigator(nav: string | undefined): SaleLang {
  const l = (nav || '').toLowerCase();
  if (l.startsWith('vi')) return 'vi';
  if (l.startsWith('zh')) return 'zh';
  if (l.startsWith('ko')) return 'ko';
  if (l.startsWith('fr')) return 'fr';
  if (l.startsWith('ja')) return 'ja';
  return 'en';
}

// ── store ngoài React (useSyncExternalStore): mọi component trên /sale dùng chung, không cần Provider ──
const listeners = new Set<() => void>();
let current: SaleLang | null = null;

function readInitial(): SaleLang {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (isSaleLang(saved)) return saved;
  } catch { /* localStorage bị chặn — dò theo trình duyệt */ }
  return langFromNavigator(typeof navigator !== 'undefined' ? navigator.language : undefined);
}
function getSnapshot(): SaleLang {
  if (current === null) current = readInitial();
  return current;
}
// SSR/hydrate đầu tiên luôn 'vi' (server không biết ngôn ngữ khách) → client đổi sang đúng ngôn ngữ ngay sau.
const getServerSnapshot = (): SaleLang => 'vi';
function subscribe(cb: () => void) { listeners.add(cb); return () => { listeners.delete(cb); }; }

export function setSaleLang(l: SaleLang) {
  current = l;
  try { localStorage.setItem(STORAGE_KEY, l); } catch { /* không lưu được — vẫn đổi cho phiên này */ }
  if (typeof document !== 'undefined') document.documentElement.lang = l;
  listeners.forEach(fn => fn());
}

// ── từ điển: ghép từ các file dict (mỗi file 1 vùng giao diện để sửa song song không đụng nhau) ──
const registry: SaleDict = { ...PAGE_DICT, ...BOOKING_DICT, ...MISC_DICT };

export function translate(lang: SaleLang, key: string, vars?: Record<string, string | number>): string {
  const e = registry[key];
  let s = e ? (e[lang] ?? e.vi) : key; // thiếu key → hiện chính key để dễ thấy khi test
  if (vars) for (const [k, v] of Object.entries(vars)) s = s.split(`{${k}}`).join(String(v));
  return s;
}

/** Ngày ISO (yyyy-mm-dd) → chuỗi theo ngôn ngữ trang. vi giữ nguyên dd/mm/yyyy (khớp fmtDate cũ);
 *  `short` = chỉ ngày/tháng. Parse tay + timeZone UTC để KHÔNG lệch ngày theo múi giờ trình duyệt. */
export function fmtSaleDate(iso: string, intl: string, short = false): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || '');
  if (!m) return iso;
  if (intl.startsWith('vi')) return short ? `${m[3]}/${m[2]}` : `${m[3]}/${m[2]}/${m[1]}`; // đúng dạng fmtDate cũ
  const d = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3]));
  const opts: Intl.DateTimeFormatOptions = short
    ? { day: '2-digit', month: '2-digit', timeZone: 'UTC' }
    : { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'UTC' };
  return new Intl.DateTimeFormat(intl, opts).format(d); // intl luôn là hằng trong SALE_LANGS → không ném lỗi
}

export function useSaleLang() {
  const lang = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  const t = useCallback((key: string, vars?: Record<string, string | number>) => translate(lang, key, vars), [lang]);
  const intl = SALE_LANGS.find(l => l.code === lang)?.intl ?? 'vi-VN';
  const fd = useCallback((iso: string) => fmtSaleDate(iso, intl), [intl]);
  const fdShort = useCallback((iso: string) => fmtSaleDate(iso, intl, true), [intl]);
  return { lang, t, intl, fd, fdShort, setLang: setSaleLang };
}
